/**
 * Request/Response Logging Service
 * Provides detailed logging of API calls for debugging
 */
/**
 * Console colors for different log types
 */
const colors = {
    request: '\x1b[36m', // Cyan
    success: '\x1b[32m', // Green
    error: '\x1b[31m', // Red
    warning: '\x1b[33m', // Yellow
    info: '\x1b[34m', // Blue
    reset: '\x1b[0m' // Reset
};
/**
 * Request Logger Class
 */
export class RequestLogger {
    constructor(config = {}) {
        this.logs = new Map();
        this.config = {
            enabled: config.enabled ?? (process.env.NODE_ENV === 'development'),
            level: config.level ?? 'basic',
            performance: config.performance ?? true,
            colorize: config.colorize ?? true
        };
        this.isDevelopment = process.env.NODE_ENV === 'development';
    }
    /**
     * Log outgoing request
     */
    logRequest(id, method, url, config) {
        if (!this.shouldLog())
            return;
        const log = {
            id,
            method,
            url,
            timestamp: Date.now()
        };
        if (this.config.level === 'headers' || this.config.level === 'full') {
            log.headers = this.sanitizeHeaders(config?.headers);
        }
        if (this.config.level === 'full' && config?.body) {
            log.body = this.sanitizeBody(config.body);
        }
        this.logs.set(id, log);
        this.printRequest(log);
    }
    /**
     * Log successful response
     */
    logResponse(id, response) {
        if (!this.shouldLog())
            return;
        const requestLog = this.logs.get(id);
        if (!requestLog)
            return;
        const duration = Date.now() - requestLog.timestamp;
        const log = {
            id,
            status: response.status,
            statusText: response.statusText || '',
            duration
        };
        if (this.config.level === 'headers' || this.config.level === 'full') {
            log.headers = response.headers;
        }
        if (this.config.level === 'full' && response.data) {
            log.data = this.sanitizeBody(response.data);
            log.size = this.calculateSize(response.data);
        }
        this.printResponse(requestLog, log);
        this.logs.delete(id);
        if (this.config.performance) {
            this.logPerformance(requestLog, log);
        }
    }
    /**
     * Log error response
     */
    logError(id, error) {
        if (!this.shouldLog())
            return;
        const requestLog = this.logs.get(id);
        if (!requestLog)
            return;
        const duration = Date.now() - requestLog.timestamp;
        const log = {
            id,
            error: this.sanitizeError(error),
            duration
        };
        this.printError(requestLog, log);
        this.logs.delete(id);
    }
    /**
     * Clear all logs
     */
    clear() {
        this.logs.clear();
    }
    /**
     * Update configuration
     */
    updateConfig(config) {
        this.config = { ...this.config, ...config };
    }
    /**
     * Check if should log
     */
    shouldLog() {
        return this.config.enabled && this.isDevelopment;
    }
    /**
     * Print request to console
     */
    printRequest(log) {
        const color = this.config.colorize ? colors.request : '';
        const reset = this.config.colorize ? colors.reset : '';
        console.group(`${color}🚀 ${log.method} ${log.url}${reset}`);
        if (log.headers) {
            console.log('Headers:', log.headers);
        }
        if (log.body) {
            console.log('Body:', log.body);
        }
        console.groupEnd();
    }
    /**
     * Print response to console
     */
    printResponse(request, response) {
        const isSuccess = response.status >= 200 && response.status < 300;
        const color = this.config.colorize ? (isSuccess ? colors.success : colors.warning) : '';
        const reset = this.config.colorize ? colors.reset : '';
        const emoji = isSuccess ? '✅' : '⚠️';
        console.group(`${color}${emoji} ${response.status} ${request.method} ${request.url} (${response.duration}ms)${reset}`);
        if (response.headers) {
            console.log('Response Headers:', response.headers);
        }
        if (response.data) {
            console.log('Response Data:', response.data);
        }
        if (response.size !== undefined) {
            console.log(`Size: ${this.formatBytes(response.size)}`);
        }
        console.groupEnd();
    }
    /**
     * Print error to console
     */
    printError(request, error) {
        const color = this.config.colorize ? colors.error : '';
        const reset = this.config.colorize ? colors.reset : '';
        console.group(`${color}❌ ERROR ${request.method} ${request.url} (${error.duration}ms)${reset}`);
        console.error('Error:', error.error);
        console.groupEnd();
    }
    /**
     * Log performance metrics
     */
    logPerformance(request, response) {
        if (response.duration > 2000) {
            console.warn(`⚠️  Slow API call: ${request.method} ${request.url} took ${response.duration}ms`);
        }
        // Could send to monitoring service here
        if (window.performance && window.performance.mark) {
            window.performance.mark(`api-${request.id}-end`);
            window.performance.measure(`api-${request.method}-${new URL(request.url).pathname}`, `api-${request.id}-start`, `api-${request.id}-end`);
        }
    }
    /**
     * Sanitize headers for logging (remove sensitive data)
     */
    sanitizeHeaders(headers) {
        if (!headers)
            return {};
        const sanitized = { ...headers };
        const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key', 'api-key'];
        sensitiveHeaders.forEach(key => {
            if (sanitized[key]) {
                sanitized[key] = '***REDACTED***';
            }
        });
        return sanitized;
    }
    /**
     * Sanitize body for logging (limit size and remove sensitive fields)
     */
    sanitizeBody(body) {
        if (!body)
            return body;
        const maxLength = 1000;
        const bodyString = JSON.stringify(body);
        if (bodyString.length > maxLength) {
            return `${bodyString.substring(0, maxLength)}... [truncated]`;
        }
        // Remove sensitive fields
        if (typeof body === 'object') {
            const sanitized = { ...body };
            const sensitiveFields = ['password', 'token', 'secret', 'apiKey'];
            sensitiveFields.forEach(field => {
                if (sanitized[field]) {
                    sanitized[field] = '***REDACTED***';
                }
            });
            return sanitized;
        }
        return body;
    }
    /**
     * Sanitize error for logging
     */
    sanitizeError(error) {
        if (error instanceof Error) {
            const { message, ...rest } = error;
            return {
                message,
                stack: this.isDevelopment ? error.stack : undefined,
                ...rest
            };
        }
        return error;
    }
    /**
     * Calculate response size in bytes
     */
    calculateSize(data) {
        const str = JSON.stringify(data);
        return new Blob([str]).size;
    }
    /**
     * Format bytes to human readable string
     */
    formatBytes(bytes) {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
}
/**
 * Singleton instance for global logging
 */
export const requestLogger = new RequestLogger();
//# sourceMappingURL=request-logger.js.map